// StoryboardCard — renders an editable marketing storyboard (message_type Storyboard).
// Overview: a 2-column grid of scene tiles (first reference image + a short prompt preview).
// Tapping a tile opens a modal with the scene's reference images (a scene may have several) on
// top and the full, readable/editable prompt below — where the client edits the prompt, sets the
// length, adds/replaces/removes reference images, or removes the scene. Add scenes from the grid;
// submit the whole set back (→ StoryboardsController → TMCHub → Adscope, which replaces the scene
// list). Paints from the message metadata so it always looks complete.
//
// Reuses the app's .signoff / .btn / .modal(-overlay) classes — no new CSS.

import { useState } from "react";
import { createPortal } from "react-dom";
import { StoryboardsApi, MediaApi } from "../hooks/useApi.js";

const uid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `s-${Date.now()}-${Math.round(Math.random() * 1e6)}`;

const DEFAULT_SECONDS = 10;
const clampSeconds = (n) => Math.max(4, Math.min(15, Math.round(Number(n) || DEFAULT_SECONDS)));

// A scene may carry several reference images. Accept the new `imageUrls` array and fall back to
// the legacy single `imageUrl` so older cards keep working.
function sceneImages(s) {
  if (Array.isArray(s.imageUrls) && s.imageUrls.length) return s.imageUrls.filter(Boolean);
  return s.imageUrl ? [s.imageUrl] : [];
}

function fromMeta(message) {
  let meta = {};
  try { meta = JSON.parse(message.metadataJson || "{}"); } catch { /* ignore */ }
  if (meta.storyId == null) return null;
  const scenes = Array.isArray(meta.scenes) ? meta.scenes : [];
  return {
    storyId: meta.storyId,
    title: meta.title || "Storyboard",
    aspect: meta.aspect || "16:9",
    scenes: scenes.map((s, i) => ({
      id: s.id ?? String(i),
      prompt: s.prompt || "",
      images: sceneImages(s),
      seconds: s.seconds ?? null,
    })),
  };
}

function frameRatio(aspect) {
  if (aspect === "9:16") return "9 / 16";
  if (aspect === "1:1") return "1 / 1";
  return "16 / 9";
}

export default function StoryboardCard({ message, onDelete }) {
  const [doc] = useState(() => fromMeta(message));
  const [scenes, setScenes] = useState(() => doc?.scenes ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null); // `${sceneId}:${idx|add}` currently uploading
  const [openIndex, setOpenIndex] = useState(null); // which scene's modal is open

  if (!doc) return <div className="msg-system">🎬 Storyboard</div>;

  const patch = (id, fields) => setScenes((list) => list.map((s) => (s.id === id ? { ...s, ...fields } : s)));
  const addScene = () => setScenes((list) => [...list, { id: uid(), prompt: "", images: [], seconds: DEFAULT_SECONDS }]);
  const removeAt = (i) => { setScenes((list) => (list.length <= 1 ? list : list.filter((_, j) => j !== i))); setOpenIndex(null); };

  const setImages = (id, next) => setScenes((list) => list.map((s) => (s.id === id ? { ...s, images: next } : s)));

  async function uploadTo(key, id, file, place) {
    if (!file) return;
    setUploadingKey(key); setError("");
    try {
      const { url } = await MediaApi.upload(file);
      setScenes((list) => list.map((s) => (s.id === id ? { ...s, images: place(s.images, url) } : s)));
    } catch { setError("Could not upload that image."); }
    finally { setUploadingKey(null); }
  }
  const addImage = (id, file) => uploadTo(`${id}:add`, id, file, (imgs, url) => [...imgs, url]);
  const replaceImageAt = (id, idx, file) =>
    uploadTo(`${id}:${idx}`, id, file, (imgs, url) => imgs.map((u, j) => (j === idx ? url : u)));
  const removeImageAt = (id, idx) => setImages(id, (scenes.find((s) => s.id === id)?.images ?? []).filter((_, j) => j !== idx));

  async function submit() {
    setBusy(true); setError("");
    try {
      await StoryboardsApi.submit(doc.storyId, {
        channelId: message.channelId,
        scenes: scenes.map((s) => ({
          id: s.id,
          prompt: s.prompt,
          imageUrls: s.images,
          imageUrl: s.images[0] ?? null,
          seconds: s.seconds ?? DEFAULT_SECONDS,
        })),
      });
      setSubmitted(true);
    } catch (e) { setError(e.body?.error || "Could not submit your changes."); }
    finally { setBusy(false); }
  }

  const ratio = frameRatio(doc.aspect);
  const editable = !submitted;
  const open = openIndex != null ? scenes[openIndex] : null;

  // A single framed image (cover-cropped). Used for tiles and the modal gallery.
  const framed = (src, alt, badge) => (
    <div style={{ position: "relative", width: "100%", aspectRatio: ratio, background: "rgba(128,128,128,.15)",
                  display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
      {src
        ? <img src={src} alt={alt} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        : <span style={{ fontSize: 22, opacity: 0.45 }}>🖼️</span>}
      {badge}
    </div>
  );

  const tile = (s, i) => framed(
    s.images[0], `Scene ${i + 1}`,
    <>
      <span style={{ position: "absolute", top: 6, left: 6, background: "rgba(0,0,0,.72)", color: "#fff",
                     fontSize: 11, fontWeight: 600, lineHeight: 1.5, borderRadius: 5, padding: "0 7px" }}>{i + 1}</span>
      {s.images.length > 1 && (
        <span style={{ position: "absolute", bottom: 6, right: 6, background: "rgba(0,0,0,.72)", color: "#fff",
                       fontSize: 10.5, fontWeight: 600, lineHeight: 1.6, borderRadius: 5, padding: "0 6px" }}>
          🖼️ {s.images.length}
        </span>
      )}
    </>,
  );

  return (
    <div className="signoff">
      <div className="signoff__head">
        <span className="signoff__icon">🎬</span>
        <div className="signoff__doc">
          <span className="signoff__eyebrow">Storyboard — shape the video</span>
          <h4>{doc.title}</h4>
          <span className="ver">{doc.aspect} · {scenes.length} scene{scenes.length === 1 ? "" : "s"}</span>
        </div>
        <span className={`status-chip status-chip--${submitted ? "done" : "pending"}`}>
          {submitted ? "✓ Submitted" : "Draft"}
        </span>
        {onDelete && <button type="button" className="meetingcard__del" title="Remove this card" onClick={() => onDelete(message)}>✕</button>}
      </div>

      {editable && (
        <p style={{ margin: "8px 0 0", fontSize: 12, opacity: 0.7 }}>
          Tap a scene to edit its prompt, set its length, or add reference images. Add or remove scenes to shape the video.
        </p>
      )}

      {/* Scene overview — 2-column grid. Tap a tile to open the editor modal. */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 12 }}>
        {scenes.map((s, i) => (
          <button key={s.id} type="button" onClick={() => setOpenIndex(i)}
                  style={{ textAlign: "left", padding: 0, border: "1px solid var(--border, #d9d4c8)", borderRadius: 10,
                           overflow: "hidden", background: "transparent", cursor: "pointer", color: "inherit", font: "inherit" }}>
            {tile(s, i)}
            <div style={{ padding: "7px 9px" }}>
              <div style={{ fontSize: 11.5, fontWeight: 600 }}>Scene {i + 1} · {s.seconds ?? DEFAULT_SECONDS}s</div>
              <div style={{ fontSize: 11.5, opacity: 0.72, marginTop: 2, lineHeight: 1.35,
                            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                {s.prompt || <em>No prompt yet — tap to add</em>}
              </div>
            </div>
          </button>
        ))}
        {editable && (
          <button type="button" onClick={addScene} disabled={busy}
                  style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4,
                           minHeight: 96, border: "1px dashed var(--border, #d9d4c8)", borderRadius: 10,
                           background: "transparent", cursor: "pointer", color: "inherit", font: "inherit", opacity: 0.85 }}>
            <span style={{ fontSize: 20 }}>＋</span><span style={{ fontSize: 12 }}>Add scene</span>
          </button>
        )}
      </div>

      {error && <div className="field__err" style={{ marginTop: 8 }}>⚠ {error}</div>}
      {submitted && <p className="stat ok" style={{ marginTop: 8 }}>✓ Your storyboard was submitted</p>}

      {editable && (
        <div className="signoff__foot" style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          <button className="btn btn--primary btn--sm" disabled={busy || scenes.length === 0} onClick={submit}>
            {busy ? <span className="btn__spin" /> : "Submit changes"}
          </button>
        </div>
      )}

      {/* Per-scene editor modal — reference images on top, full readable prompt below. */}
      {open && createPortal(
        <div className="modal-overlay is-open" onClick={() => setOpenIndex(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="modal__head">
              <h3>Scene {openIndex + 1} <span style={{ opacity: 0.6, fontWeight: 400 }}>of {scenes.length}</span></h3>
              <button type="button" className="modal__close" onClick={() => setOpenIndex(null)} aria-label="Close">✕</button>
            </div>
            <div className="modal__body">
              {/* Reference-image gallery. A scene can have several; each guides the AI render. */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 6 }}>
                  Reference images {open.images.length ? `(${open.images.length})` : ""}
                </label>
                <div style={{ display: "grid", gridTemplateColumns: open.images.length > 1 ? "1fr 1fr" : "1fr", gap: 6 }}>
                  {open.images.map((url, idx) => (
                    <div key={idx} style={{ position: "relative" }}>
                      {framed(url, `Scene ${openIndex + 1} · reference ${idx + 1}`)}
                      {editable && (
                        <div style={{ position: "absolute", top: 6, right: 6, display: "flex", gap: 5 }}>
                          <label className="btn btn--sm" style={{ cursor: "pointer", padding: "2px 7px", fontSize: 11 }} title="Replace">
                            {uploadingKey === `${open.id}:${idx}` ? <span className="btn__spin" /> : "↻"}
                            <input type="file" accept="image/*" disabled={busy || uploadingKey != null} style={{ display: "none" }}
                                   onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; replaceImageAt(open.id, idx, f); }} />
                          </label>
                          <button type="button" className="btn btn--sm" title="Remove"
                                  style={{ padding: "2px 7px", fontSize: 11, color: "#c0392b" }}
                                  disabled={busy || uploadingKey != null} onClick={() => removeImageAt(open.id, idx)}>✕</button>
                        </div>
                      )}
                    </div>
                  ))}
                  {editable && (
                    <label style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4,
                                    aspectRatio: ratio, border: "1px dashed var(--border, #d9d4c8)", borderRadius: 8,
                                    cursor: "pointer", opacity: 0.85 }} title="Add reference image">
                      {uploadingKey === `${open.id}:add`
                        ? <span className="btn__spin" />
                        : <><span style={{ fontSize: 20 }}>＋</span><span style={{ fontSize: 11.5 }}>Add image</span></>}
                      <input type="file" accept="image/*" disabled={busy || uploadingKey != null} style={{ display: "none" }}
                             onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; addImage(open.id, f); }} />
                    </label>
                  )}
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12.5, opacity: 0.8 }}>
                  Length
                  <input type="number" min={4} max={15} value={open.seconds ?? DEFAULT_SECONDS} disabled={!editable}
                         onChange={(e) => patch(open.id, { seconds: clampSeconds(e.target.value) })}
                         style={{ width: 48, padding: "3px 5px", borderRadius: 6, border: "1px solid var(--border, #d9d4c8)", font: "inherit", textAlign: "center" }} />
                  sec
                </label>
                {editable && scenes.length > 1 && (
                  <button type="button" className="btn btn--sm" onClick={() => removeAt(openIndex)} disabled={busy}
                          style={{ marginLeft: "auto", color: "#c0392b" }}>Remove scene</button>
                )}
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 5 }}>Prompt</label>
                <textarea value={open.prompt} disabled={!editable} rows={8} autoFocus
                          placeholder="Describe what happens in this scene…"
                          onChange={(e) => patch(open.id, { prompt: e.target.value })}
                          style={{ width: "100%", resize: "vertical", padding: 10, borderRadius: 8, lineHeight: 1.5,
                                   border: "1px solid var(--border, #d9d4c8)", font: "inherit" }} />
              </div>
            </div>
            <div className="modal__foot" style={{ justifyContent: "space-between" }}>
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" className="btn btn--sm" disabled={openIndex === 0} onClick={() => setOpenIndex((i) => Math.max(0, i - 1))}>← Prev</button>
                <button type="button" className="btn btn--sm" disabled={openIndex >= scenes.length - 1} onClick={() => setOpenIndex((i) => Math.min(scenes.length - 1, i + 1))}>Next →</button>
              </div>
              <button type="button" className="btn btn--primary btn--sm" onClick={() => setOpenIndex(null)}>Done</button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
